import React, { useEffect, useState, useMemo, useRef } from "react";
import { Pie, Column } from "@ant-design/plots";
import { Dropdown } from "semantic-ui-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useWindowWidth } from "../../../../hooks/useWindowWidth";
import { getDonutHeight, BAR_PALETTE, DONUT_COLORS } from "../../../../utils/chartConfig";
import { EmptyChartMessage } from "../EmptyChartMessage";
import { renderDonutForPdf, renderBarForPdf, drawChartRow } from "../pdfCharts";
import {
  Energyform,
  Fuelsform,
  Waterform,
  Productionform,
  Socialform,
  Form,
  Site,
  Wasteform,
  Effluentform,
  Company,
} from "../../../../api";
import { useAuth } from "../../../../hooks";
import { isAdmin, isMaster } from "../../../../utils/checkPermission";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { LoadingSpinner } from "../../../Shared";
import { convertEnergy2 } from "../../../../utils/dataAndFormulas/convertEnergy";
import { convertUnitsFinal } from "../../../../utils/dataAndFormulas/energyForms";
import { convertUnitsFinal as convertEffluent } from "../../../../utils/dataAndFormulas/effluentForms";
import { calculateEmissionsByType, geiCodes } from "../../../../utils/dataAndFormulas/GEIForms";
import { formatWhole, formatMagnitude, formatMagnitudeAdaptive, formatPercentage } from "../../../../utils/numberFormat";
import "./GeneralDashboard.scss";

const energyController = new Energyform();
const fuelsController = new Fuelsform();
const waterController = new Waterform();
const productionController = new Productionform();
const socialController = new Socialform();
const formController = new Form();
const siteController = new Site();
const companyController = new Company();
const wasteController = new Wasteform();
const effluentController = new Effluentform();

// Locale para formatear la fecha "Generado el ..." del PDF — antes fijo en
// "es-ES" sin importar el idioma de la app (mostraba el mes siempre en
// español).
const DATE_LOCALES = { es: "es-ES", en: "en-US", fr: "fr-FR", pt: "pt-BR" };

async function fetchWithRetry(fn, retries = 3, delayMs = 800) {
  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const result = await fn();
      if (result && (result.code === 503 || result.status === 503)) throw new Error("503");
      return { status: "fulfilled", value: result };
    } catch (err) {
      lastError = err;
      if (attempt === retries) {
        console.error("[GeneralDashboard] fetch failed after retries:", err);
        return { status: "rejected", reason: lastError };
      }
      await new Promise(r => setTimeout(r, delayMs * (attempt + 1)));
    }
  }
}

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ENERGY_GROUPS = {
  electricity: [
    "electricity_standard_combined",
    "electricity_standard_domestic",
    "electricity_standard_production",
  ],
  renewablesElec: [
    "renewable_energies_ppa_combined",
    "renewable_energies_ppa_domestic",
    "renewable_energies_ppa_production",
    "renewable_energies_on_site_combined",
    "renewable_energies_on_site_domestic",
    "renewable_energies_on_site_production",
    "irec_certificates",
  ],
  fuels: [
    "natural_gas_combined", "natural_gas_cogeneration", "natural_gas_production_operations", "natural_gas_domestic",
    "glp_combined", "glp_cogeneration", "glp_production_operations", "glp_domestic",
    "heavy_fuel_oil_total", "light_fuel_oil_total", "coal_total",
    "diesel_combined", "diesel_cogeneration", "diesel_production_operations", "diesel_forklifts",
    "biodiesel_combined", "biodiesel_cogeneration", "biodiesel_production_operations", "biodiesel_forklifts",
    "gasoline_combined", "gasoline_cogeneration", "gasoline_production_operations", "gasoline_forklifts",
    "biofuel_combined", "biofuel_cogeneration", "biofuel_production_operations", "biofuel_forklifts",
    "biomass_total", "steam_total",
  ],
};

const WATER_GROUPS = {
  redMunicipal: ["water_domestic_municipal_network", "municipal_network_water_production", "combined_municipal_network_water"],
  subterranea: ["domestic_groundwater", "groundwater_production", "groundwater_combined"],
  alternativas: ["rainwater", "reused_water", "recycled_water", "steam_condensed_water"],
  superficial: ["surface_water"],
  destilada: ["distilled_water"],
};

// Field names match wasteCodes (used by total_non_hazardous_waste_kpis + total_hazardous_waste_kpis in WasteKPIsDashboard)
const WASTE_KEYS = [
  // No peligrosos
  "paper_and_cardboard", "plastic", "fabric_scraps", "metal_clean", "wood",
  "food_waste", "general_trash", "glass", "leather", "foam_eva",
  "empty_cans_or_containers", "rubber", "other_non_hazardous_waste",
  // Peligrosos
  "electronic_waste", "empty_chemical_containers", "cleaning_pesticide_containers",
  "batteries", "fluorescent_tubes", "cooking_oil", "maintenance_oil", "lubricants",
  "paint_waste", "coal_ashes", "rubber_hazardous", "chemicals",
  "contaminated_materials", "ink_cartridges", "medical_waste", "other_hazardous_waste",
];

const ENERGY_SRC_COLORS = {
  "Electricidad":  "#0E376E",
  "Renovables":    "#648EBC",
  "Combustibles":  "#A4C4E0",
};

const WATER_SRC_COLORS = {
  "Red municipal": "#0E376E",
  "Subterránea":   "#2E65A0",
  "Alternativas":  "#7EAAD0",
  "Otras":         "#B8D4EA",
};

// `type` queda fijo en español porque es la clave interna que matchea
// contra ENERGY_SRC_COLORS/WATER_SRC_COLORS (colores) — el texto que se
// muestra (pills, tooltip, leyenda del PDF) se traduce por separado con
// estos mapas, sin tocar `type`. Mismo patrón que GENDER_TYPE_LABEL_KEYS
// en SocialKPIsDashboard/TableGraf.js.
const ENERGY_SRC_LABEL_KEYS = {
  "Electricidad": "energy_source_electricity",
  "Renovables": "energy_source_renewables",
  "Combustibles": "energy_source_fuels",
};

const WATER_SRC_LABEL_KEYS = {
  "Red municipal": "water_source_municipal",
  "Subterránea": "water_source_groundwater",
  "Alternativas": "water_source_alternative",
  "Otras": "water_source_other",
};

function toTons(total, unit) {
  if (!total || !isFinite(total)) return 0;
  const u = (unit || "t").toLowerCase().replace(/\s/g, "");
  if (u === "kg" || u === "kilogram" || u === "kilogramo") return total / 1000;
  if (u === "g" || u === "gram" || u === "gramo") return total / 1000000;
  if (u === "lb" || u === "libra") return total * 0.000453592;
  return total; // t, ton, tonelada, tn → already tons
}

function toMWh(entry, field) {
  if (!entry?.value) return 0;
  try {
    const unit = (entry.unit || "kwh").toLowerCase();
    if (unit === "mwh") return Number(entry.value);
    if (unit === "kwh") return Number(entry.value) / 1000;
    return Number(convertEnergy2(entry.value, entry.unit, "MWh", field, true)) || 0;
  } catch {
    return 0;
  }
}

// Extracts the unit code from display labels like "Litros (L)" → "L", "Metros cúbicos (m3)" → "m3".
// If no parentheses pattern found, returns the string as-is.
function extractUnitCode(unit) {
  if (!unit) return unit;
  const match = unit.match(/\(([^)]+)\)\s*$/);
  return match ? match[1].trim() : unit;
}

// Like toMWh but normalizes to the system-configured unit first (same as transformData1 in EnergyKPIsDashboard).
// configUnits: map of field → configured unit string from getLastUnitFieldsByYear.
function toMWhWithConfig(entry, field, configUnits) {
  if (!entry?.value) return 0;
  try {
    const value = Number(entry.value) || 0;
    if (!value) return 0;
    // Extract unit code from display labels (e.g. "Litros (L)" → "L")
    const configUnit = extractUnitCode(configUnits?.[field]);
    const formUnit = extractUnitCode(entry.unit) || configUnit;
    if (!formUnit) return toMWh(entry, field);
    let normalizedValue = value;
    let normalizedUnit = formUnit;
    if (configUnit && formUnit.toLowerCase() !== configUnit.toLowerCase()) {
      normalizedValue = convertUnitsFinal(value, formUnit, configUnit, field) || 0;
      normalizedUnit = configUnit;
    }
    const u = normalizedUnit.toLowerCase();
    if (u === "mwh") return normalizedValue;
    if (u === "kwh") return normalizedValue / 1000;
    return Number(convertEnergy2(normalizedValue, normalizedUnit, "MWh", field, true)) || 0;
  } catch {
    return 0;
  }
}

function toM3(entry) {
  if (!entry?.value) return 0;
  const val = Number(entry.value) || 0;
  const unit = (entry.unit || "m3").toLowerCase();
  if (unit === "l" || unit === "lt" || unit === "litros") return val / 1000;
  return val;
}

export function GeneralDashboard({ company, site }) {
  const { accessToken, user: { role } } = useAuth();
  const canSwitchSite = isMaster(role) || isAdmin(role);
  const { translations, language } = useLanguage();
  const t = (key) => translations[key] || key;

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [availableYears, setAvailableYears] = useState([currentYear]);

  const [activeSite, setActiveSite] = useState("all");
  const [sitesList, setSitesList] = useState([]);

  const [comparativaRows, setComparativaRows] = useState([]);
  const [comparativaLoading, setComparativaLoading] = useState(true);

  const [energyForms, setEnergyForms] = useState([]);
  const [energyConfigUnits, setEnergyConfigUnits] = useState({});
  const [waterForms, setWaterForms] = useState([]);
  const [productionForms, setProductionForms] = useState({});
  const [workersByPeriod, setWorkersByPeriod] = useState({});
  const [baselineType, setBaselineType] = useState({});

  const [energyKPIs, setEnergyKPIs] = useState({ total: 0, renewablePct: 0, intensity: 0 });
  const [waterKPIs, setWaterKPIs] = useState({ total: 0, altPct: 0, intensity: 0 });
  const [geiKPIs, setGeiKPIs] = useState({ total: 0, alcance2: 0 });

  const [loadingCount, setLoadingCount] = useState(0);
  const isLoading = loadingCount > 0;
  const [companyName, setCompanyName] = useState("");
  const [fetchError, setFetchError] = useState(false);

  const startLoading = () => setLoadingCount(p => p + 1);
  const stopLoading = () => setLoadingCount(p => Math.max(0, p - 1));

  const dashRef = useRef(null);
  const comparativaRef = useRef(null);
  const [exportingPdf, setExportingPdf] = useState(false);

  // const [zoom, setZoom] = useState(() => {
  //   const saved = localStorage.getItem("general_dashboard_zoom");
  //   return saved ? Number(saved) : 70;
  // });
  // const handleZoom = (delta) => {
  //   setZoom(prev => {
  //     const next = Math.min(120, Math.max(40, prev + delta));
  //     localStorage.setItem("general_dashboard_zoom", next);
  //     return next;
  //   });
  // };

  const handleExportPdf = async () => {
    if (!dashRef.current || exportingPdf) return;
    setExportingPdf(true);
    // Hidden off-screen wrapper used for clone-based capture (zoom-independent)
    let offscreenWrapper = null;
    const chartCleanups = [];
    try {
      // ── 1. Wait for fonts and G2 chart animations ─────────────────────
      await document.fonts.ready;
      await new Promise(r => setTimeout(r, 600));
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      // ── 2. Renderizar los 6 gráficos como SVG vectorial, fuera de
      // pantalla, en paralelo — reemplaza la captura por html2canvas que
      // tenían antes las dos filas de gráficos (ver pdfCharts.js: ejes,
      // grillas y colores vienen bien dentro del SVG; el texto "Total" del
      // centro de la dona se dibuja aparte porque @ant-design/plots lo arma
      // como nodo imperativo fuera del <svg>, validado con un prototipo).
      const [energyDonut, energyBar, productionBar, waterDonut, waterBar, workersBar] = await Promise.all([
        renderDonutForPdf(
          energySourcesData.map((d) => ({ ...d, label: t(ENERGY_SRC_LABEL_KEYS[d.type] || d.type) })),
          ENERGY_SRC_COLORS,
        ),
        renderBarForPdf(energyYearlyData, "MWh", BAR_PALETTE),
        renderBarForPdf(productionYearlyData, t("units"), BAR_PALETTE),
        renderDonutForPdf(
          waterSourcesData.map((d) => ({ ...d, label: t(WATER_SRC_LABEL_KEYS[d.type] || d.type) })),
          WATER_SRC_COLORS,
        ),
        renderBarForPdf(waterYearlyData, "m³", BAR_PALETTE),
        renderBarForPdf(workersYearlyData, "FTE", BAR_PALETTE),
      ]);
      [energyDonut, energyBar, productionBar, waterDonut, waterBar, workersBar].forEach(r => {
        if (r.cleanup) chartCleanups.push(r.cleanup);
      });

      const chartRow1Cards = [
        { title: t("energy_sources"), ...energyDonut },
        { title: t("electric_energy_consumption"), ...energyBar },
        { title: t("production"), ...productionBar },
      ];
      const chartRow2Cards = [
        { title: t("water_sources"), ...waterDonut },
        { title: t("water_consumption"), ...waterBar },
        { title: t("workers"), ...workersBar },
      ];

      // ── 3. Build off-screen container at a FIXED width ────────────────
      // Solo hace falta para las filas de KPI (texto/CSS, sin gráficos) —
      // las filas de gráficos ya no pasan por acá, se dibujan nativas más
      // abajo (paso 8) con lo que se armó en el paso 2.
      const FIXED_W = 1400;
      offscreenWrapper = document.createElement("div");
      offscreenWrapper.style.cssText = [
        // position:absolute (no :fixed) — algunos navegadores dejan de pintar
        // contenido position:fixed que cae fuera del viewport vertical en
        // páginas largas (optimización de paint), lo que recortaba la última
        // fila de tarjetas en la captura. absolute queda sujeto al flujo del
        // documento en vez del viewport, así que se pinta completo sin
        // importar cuánto mida verticalmente.
        `position:absolute`,
        `left:-${FIXED_W + 200}px`,
        `top:0`,
        `width:${FIXED_W}px`,
        `overflow:visible`,
        `pointer-events:none`,
        `background:#fff`,
      ].join(";");
      document.body.appendChild(offscreenWrapper);

      // ── 4. Clone dashboard into the wrapper ───────────────────────────
      const clone = dashRef.current.cloneNode(true);
      offscreenWrapper.appendChild(clone);

      // ── 5. Apply style fixes to the clone (solo filas de KPI) ─────────
      clone.querySelectorAll(".card-title").forEach(el => {
        el.style.setProperty("font-size", "16px", "important");
        el.style.setProperty("line-height", "1.3", "important");
      });

      // Wait for reflow at FIXED_W
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));

      // ── 6. Capturar filas de KPI (texto/CSS) por fila, con html2canvas ──
      // Las filas de gráficos (.generalDashboardGrid) se excluyen: ya se
      // renderizaron aparte, como vector, en el paso 2.
      const captureOpts = {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        ignoreElements: el => el.dataset?.pdfExclude === "true",
      };
      const rowBlocks = [...clone.children].filter(
        el => el.offsetHeight > 0 && !el.classList.contains("generalDashboardGrid")
      );
      const kpiBlockCanvases = [];
      for (const block of rowBlocks) {
        // Math.max de scrollHeight y getBoundingClientRect (más un margen
        // chico) como resguardo: si alguna mide corto por el motivo que sea,
        // la otra (o el margen) evita que se recorte contenido real — el
        // peor caso posible es un poco de blanco de más, nunca contenido
        // cortado.
        const blockH = Math.max(block.scrollHeight, block.getBoundingClientRect().height) + 16;
        const c = await html2canvas(block, {
          ...captureOpts,
          width: FIXED_W,
          height: blockH,
        });
        kpiBlockCanvases.push(c);
      }

      // ── 7. Optionally capture comparativa (page 2) ────────────────────
      let canvas2 = null;
      if (comparativaRef.current && comparativaRef.current.offsetHeight > 10) {
        const compClone = comparativaRef.current.cloneNode(true);
        offscreenWrapper.appendChild(compClone);
        await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
        const compCloneH = Math.max(compClone.scrollHeight, compClone.getBoundingClientRect().height) + 24;
        canvas2 = await html2canvas(compClone, {
          ...captureOpts,
          width: FIXED_W,
          height: compCloneH,
        });
      }

      // ── 8. Generate PDF ───────────────────────────────────────────────
      // Página A4 estándar y fija (mismo criterio que KPIExportIconButton) en
      // vez de una hoja a medida del contenido -- antes la altura se calculaba
      // a partir de la proporción del canvas capturado, y como el dashboard se
      // arma ancho y bajo (FIXED_W=1400 con varias columnas), la hoja quedaba
      // mucho más chata/ancha que una A4 real. Ahora el contenido se escala
      // para entrar en la página, no al revés.
      const margin = 20;
      const headerH = companyName ? 68 : 56;

      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const availW = pageW - margin * 2;

      const siteName = activeSite !== "all"
        ? (sitesList.find(s => s._id === activeSite)?.name || "Sitio")
        : "Total Grupo";

      const addPageHeader = (title) => {
        doc.setFontSize(14);
        doc.setFont(undefined, "bold");
        doc.setTextColor(20, 40, 80);
        doc.setCharSpace(1);
        doc.text(title, margin, 28);
        doc.setCharSpace(0);
        let dateY = 42;
        if (companyName) {
          doc.setFontSize(10);
          doc.setFont(undefined, "normal");
          doc.setTextColor(60, 80, 120);
          doc.text(companyName.toLocaleUpperCase(), margin, 42);
          dateY = 54;
        }
        doc.setFontSize(8);
        doc.setFont(undefined, "normal");
        doc.setTextColor(130, 130, 130);
        doc.text(
          `${t("generated_on")} ${new Date().toLocaleDateString(DATE_LOCALES[language] || "es-ES", { day: "2-digit", month: "long", year: "numeric" })}`,
          margin, dateY
        );
        doc.setDrawColor(210, 210, 210);
        doc.line(margin, dateY + 6, pageW - margin, dateY + 6);
      };

      // Recorta un canvas en franjas horizontales de a lo sumo una página y
      // reparte cada una en su propia hoja. Se usa como fallback para un
      // bloque que por sí solo no entra en una página (raro — solo puede
      // pasar con las filas de KPI, capturadas; las de gráficos miden
      // siempre CHART_ROW_H, fijo y bien por debajo de una página), y para
      // la comparativa (una sola tabla, no filas independientes).
      const pageContentH = pageH - margin - headerH; // alto disponible por página, en pt
      const addSlicedSection = (canvas, title, isFirstPage) => {
        const pxPerPt = canvas.width / availW;
        const sliceHpx = Math.max(1, Math.floor(pageContentH * pxPerPt));
        const totalSlices = Math.max(1, Math.ceil(canvas.height / sliceHpx));

        for (let i = 0; i < totalSlices; i++) {
          if (!isFirstPage || i > 0) doc.addPage("a4", "landscape");
          addPageHeader(totalSlices > 1 ? `${title} (${i + 1}/${totalSlices})` : title);

          const sy = i * sliceHpx;
          const sh = Math.min(sliceHpx, canvas.height - sy);
          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sh;
          sliceCanvas.getContext("2d").drawImage(canvas, 0, sy, canvas.width, sh, 0, 0, canvas.width, sh);
          const drawH = sh / pxPerPt;
          doc.addImage(sliceCanvas.toDataURL("image/png"), "PNG", margin, headerH, availW, drawH);
        }
      };

      // Acomoda los bloques (filas de KPI capturadas + filas de gráficos
      // dibujadas nativas) uno debajo del otro; si el próximo no entra en lo
      // que queda de la hoja actual, arranca una hoja nueva — el salto nunca
      // parte un bloque al medio.
      const CHART_ROW_H = 210;
      const title1 = `${t("general_dashboard")} — ${siteName} — ${selectedYear}`;
      const blocks = [
        ...kpiBlockCanvases.map(canvas => ({
          height: (canvas.height / canvas.width) * availW,
          canvas, // presente => bloque "recortable" si no entra en una página
          draw: async (yPos) => {
            const scaledH = (canvas.height / canvas.width) * availW;
            doc.addImage(canvas.toDataURL("image/png"), "PNG", margin, yPos, availW, scaledH);
          },
        })),
        { height: CHART_ROW_H, draw: (yPos) => drawChartRow(doc, chartRow1Cards, { x: margin, y: yPos, availW, cardH: CHART_ROW_H, emptyMessage: t("no_data_available") }) },
        { height: CHART_ROW_H, draw: (yPos) => drawChartRow(doc, chartRow2Cards, { x: margin, y: yPos, availW, cardH: CHART_ROW_H, emptyMessage: t("no_data_available") }) },
      ];

      let cursorY = null;
      const GAP = 10;
      for (let idx = 0; idx < blocks.length; idx++) {
        const block = blocks[idx];
        const isVeryFirstBlock = idx === 0;

        if (block.height > pageContentH) {
          if (block.canvas) {
            addSlicedSection(block.canvas, title1, isVeryFirstBlock);
          } else {
            if (!isVeryFirstBlock) doc.addPage("a4", "landscape");
            addPageHeader(title1);
            await block.draw(headerH);
          }
          cursorY = null; // la próxima fila arranca hoja nueva y limpia
          continue;
        }

        const needsNewPage = cursorY === null || cursorY + block.height > pageH - margin;
        if (needsNewPage) {
          // jsPDF ya crea la primera hoja al construir el doc — no duplicarla.
          if (!isVeryFirstBlock) doc.addPage("a4", "landscape");
          addPageHeader(title1);
          cursorY = headerH;
        }

        await block.draw(cursorY);
        cursorY += block.height + GAP;
      }

      if (canvas2) {
        addSlicedSection(canvas2, `Comparativa de indicadores — ${siteName} — ${selectedYear}`, false);
      }

      const slug = siteName.replace(/\s+/g, "-");
      doc.save(`Dashboard-General_${slug}_${selectedYear}.pdf`);
    } catch (err) {
      console.error("Error exportando PDF:", err);
    } finally {
      if (offscreenWrapper) document.body.removeChild(offscreenWrapper);
      chartCleanups.forEach(fn => fn());
      setExportingPdf(false);
    }
  };

  // Fetch company name when company changes
  useEffect(() => {
    if (!company || company === "0" || company === "") { setCompanyName(""); return; }
    (async () => {
      try {
        const res = await companyController.getCompany(accessToken, company);
        setCompanyName(res?.name || "");
      } catch {
        setCompanyName("");
      }
    })();
  }, [company, accessToken]);

  // Fetch sites list when company changes. Separado del efecto de abajo (que
  // solo sigue al sitio activo) para no re-disparar este fetch en cada
  // cambio de sitio — antes ambas cosas vivían en el mismo efecto con
  // deps [company, site, accessToken], así que cambiar de sitio también
  // reiniciaba el fetch de sitios, y sin guard contra respuestas viejas una
  // respuesta tardía de una empresa anterior podía pisar la lista correcta.
  useEffect(() => {
    if (!company || company === "0" || company === "") {
      setSitesList([]);
      return;
    }
    // Reset síncrono antes del fetch: si no se limpia acá, sitesList se queda
    // con los sitios de la empresa anterior mientras el fetch está en vuelo.
    // Como activeSite (efecto de abajo) se actualiza síncrono al cambiar de
    // empresa, el "Main data fetch" podía dispararse una vez de más con el
    // activeSite ya nuevo pero sitesList todavía vieja — arma sitesToFetch
    // con IDs de sitio de la empresa equivocada y genera una ráfaga extra de
    // pedidos concurrentes justo en la transición.
    setSitesList([]);
    let cancelled = false;
    (async () => {
      try {
        const res = await siteController.getSitesByCompany(accessToken, company, true);
        if (!cancelled) setSitesList(res || []);
      } catch {
        if (!cancelled) setSitesList([]);
      }
    })();
    return () => { cancelled = true; };
  }, [company, accessToken]);

  // El sitio activo sigue al selector del header (site prop) — sin sitio
  // elegido ahí, es "all" (grupo).
  useEffect(() => {
    if (!company || company === "0" || company === "") {
      setActiveSite("all");
      return;
    }
    setActiveSite(site || "all");
  }, [company, site]);

  // Main data fetch: handles single site or aggregated group total
  useEffect(() => {
    const sitesToFetch = activeSite === "all"
      ? sitesList.map(s => s._id)
      : activeSite ? [activeSite] : [];

    if (!sitesToFetch.length) return;

    const years = Array.from({ length: 5 }, (_, i) => String(currentYear - i));

    let cancelled = false;

    setEnergyForms([]);
    setEnergyConfigUnits({});
    setWaterForms([]);
    setProductionForms({});
    setWorkersByPeriod({});
    setFetchError(false);

    (async () => {
      startLoading();
      try {
        // Config de unidades del sitio primario se pide EN PARALELO con el
        // resto (antes se pedía después, con su propio await y su propio
        // setEnergyConfigUnits separado). Eso dejaba una ventana en toda
        // carga donde energyForms ya estaba seteado pero energyConfigUnits
        // seguía en el default {} — el efecto que calcula energyKPIs corría
        // una vez con unidades sin normalizar ("absolutas") y, al no tener
        // energyConfigUnits en sus dependencias, no se recalculaba solo;
        // quedaba pegado así hasta que el usuario cambiaba de año/sitio.
        // Pidiéndolo en paralelo y confirmando todo en el mismo batch
        // síncrono de abajo, esa ventana desaparece desde la primera pintura.
        const primarySiteId = sitesToFetch[0];
        const allEnergyFields = [
          ...ENERGY_GROUPS.electricity,
          ...ENERGY_GROUPS.renewablesElec,
          ...ENERGY_GROUPS.fuels,
        ];

        const [allResults, unitsResult] = await Promise.all([
          Promise.allSettled(
            sitesToFetch.map(siteId =>
              Promise.all([
                fetchWithRetry(() => energyController.getEnergyFormsByLastYears(accessToken, siteId, years)),
                fetchWithRetry(() => waterController.getWaterFormsByLastYears(accessToken, siteId, years)),
                fetchWithRetry(() => productionController.getProductionFormsBySiteAndYears(accessToken, siteId, years)),
                fetchWithRetry(() => socialController.getTotalWorkersByYearsAndPeriod(accessToken, siteId, years)),
                fetchWithRetry(() => formController.getLastBaselineByFormBySite(accessToken, siteId, "production")),
              ])
            )
          ),
          fetchWithRetry(() => energyController.getLastUnitFieldsByYear(
            accessToken, primarySiteId, String(currentYear), allEnergyFields
          )),
        ]);

        if (cancelled) return;

        let allEnergyForms = [];
        let allWaterForms = [];
        let mergedProductionForms = {};
        let mergedWorkersByPeriod = {};
        let baselineResult = {};
        let anyFailed = false;

        allResults.forEach((siteRes, idx) => {
          if (siteRes.status !== "fulfilled") { anyFailed = true; return; }
          const [energyRes, waterRes, prodRes, workersRes, baselineRes] = siteRes.value;

          if (energyRes.status === "fulfilled") {
            allEnergyForms = [...allEnergyForms, ...(energyRes.value?.energyForms?.data || [])];
          } else {
            anyFailed = true;
          }
          if (waterRes.status === "fulfilled") {
            allWaterForms = [...allWaterForms, ...(waterRes.value?.waterForms?.data || [])];
          } else {
            anyFailed = true;
          }
          if (prodRes.status === "fulfilled") {
            const prodData = prodRes.value?.productionForms || {};
            Object.entries(prodData).forEach(([year, docs]) => {
              mergedProductionForms[year] = [
                ...(mergedProductionForms[year] || []),
                ...(Array.isArray(docs) ? docs : []),
              ];
            });
          }
          if (workersRes.status === "fulfilled") {
            const wData = workersRes.value?.totalWorkersByPeriod || {};
            Object.entries(wData).forEach(([year, months]) => {
              if (!mergedWorkersByPeriod[year]) mergedWorkersByPeriod[year] = {};
              Object.entries(months).forEach(([month, val]) => {
                mergedWorkersByPeriod[year][month] =
                  (mergedWorkersByPeriod[year][month] || 0) + (Number(val) || 0);
              });
            });
          }
          if (idx === 0 && baselineRes.status === "fulfilled" && baselineRes.value?.code === 200) {
            baselineResult = baselineRes.value.baseline || {};
          }
        });

        // Todo lo que depende de allEnergyForms/allWaterForms (incluido el
        // rango de años) Y la config de unidades se confirma en el mismo
        // batch síncrono — así ninguna pintura ve energyForms nuevo junto
        // con energyConfigUnits todavía viejo/default.
        setEnergyForms(allEnergyForms);
        setWaterForms(allWaterForms);
        setProductionForms(mergedProductionForms);
        setWorkersByPeriod(mergedWorkersByPeriod);
        setBaselineType(baselineResult);
        setEnergyConfigUnits(unitsResult.status === "fulfilled" ? (unitsResult.value?.fields || {}) : {});
        // Only surface the error banner when a failure actually left us without
        // data to show — a partial failure alongside real data isn't worth alarming over.
        setFetchError(anyFailed && allEnergyForms.length === 0 && allWaterForms.length === 0);

        const allFormYears = [
          ...allEnergyForms.map(f => Number(f.year)),
          ...allWaterForms.map(f => Number(f.year)),
        ];
        const uniqueYears = [...new Set(allFormYears)].filter(Boolean).sort((a, b) => b - a);
        if (uniqueYears.length > 0) {
          setAvailableYears(uniqueYears);
          if (!uniqueYears.includes(selectedYear)) setSelectedYear(uniqueYears[0]);
        }
      } finally {
        stopLoading();
      }
    })();

    return () => { cancelled = true; };
  }, [activeSite, sitesList, accessToken]);

  useEffect(() => {
    const yearStr = String(selectedYear);
    const yearEnergyForms = energyForms.filter(f => String(f.year) === yearStr);
    const yearWaterForms = waterForms.filter(f => String(f.year) === yearStr);

    // Energy KPIs
    let totalElecMWh = 0, totalRenewMWh = 0, totalFuelsMWh = 0;
    yearEnergyForms.forEach(form => {
      ENERGY_GROUPS.electricity.forEach(field => { totalElecMWh += toMWhWithConfig(form[field], field, energyConfigUnits); });
      ENERGY_GROUPS.renewablesElec.forEach(field => { totalRenewMWh += toMWhWithConfig(form[field], field, energyConfigUnits); });
      ENERGY_GROUPS.fuels.forEach(field => { totalFuelsMWh += toMWhWithConfig(form[field], field, energyConfigUnits); });
    });
    const totalEnergyMWh = totalElecMWh + totalRenewMWh + totalFuelsMWh;
    const renewablePct = totalEnergyMWh > 0 ? (totalRenewMWh / totalEnergyMWh) * 100 : 0;

    // Production for selected year (used for intensity calculations)
    const blType = baselineType[yearStr] || "VPS";
    const prefix = blType === "VPS" ? "VP" : "VF";
    let production = 0;
    (productionForms[yearStr] || []).forEach(doc => {
      Object.values(doc).forEach(campo => {
        if (campo?.code?.startsWith(prefix) && !campo.code.startsWith(prefix + "S")) {
          production += (Number(campo.value) || 0) * (Number(campo.standardized_coefficient) || 1);
        }
      });
    });

    const energyIntensity = production > 0 ? (totalEnergyMWh * 1000) / production : 0;
    setEnergyKPIs({ total: totalEnergyMWh, renewablePct, intensity: energyIntensity });

    // GEI KPIs
    const alcance2 = (totalElecMWh * 1000 * 0.43) / 1000;
    let scope1 = 0;
    yearEnergyForms.forEach(form => {
      ENERGY_GROUPS.fuels.forEach(field => {
        const entry = form[field];
        if (entry?.value) {
          try {
            const result = calculateEmissionsByType(Number(entry.value), entry.unit, field);
            if (result?.alcance === 1) scope1 += result.emisiones || 0;
          } catch {}
        }
      });
    });
    setGeiKPIs({ total: scope1 + alcance2, alcance2 });

    // Water KPIs
    let totalWaterM3 = 0, altWaterM3 = 0;
    const allWaterFields = Object.values(WATER_GROUPS).flat();
    yearWaterForms.forEach(form => {
      allWaterFields.forEach(field => { totalWaterM3 += toM3(form[field]); });
      WATER_GROUPS.alternativas.forEach(field => { altWaterM3 += toM3(form[field]); });
    });
    const altPct = totalWaterM3 > 0 ? (altWaterM3 / totalWaterM3) * 100 : 0;
    const waterIntensity = production > 0 ? (totalWaterM3 * 1000) / production : 0;
    setWaterKPIs({ total: totalWaterM3, altPct, intensity: waterIntensity });
  }, [energyForms, waterForms, selectedYear, productionForms, baselineType, energyConfigUnits]);

  // Comparison table: fetch per-site data when company or year changes.
  // Waits for the main data fetch to finish so selectedYear is already resolved.
  useEffect(() => {
    if (isLoading) return;
    if (!company || company === "" || company === "0") {
      setComparativaRows([]);
      setComparativaLoading(false);
      return;
    }
    const yearStr = String(selectedYear);
    let cancelled = false;
    (async () => {
      setComparativaLoading(true);
      try {
        const sitesRes = await siteController.getSitesByCompany(accessToken, company, true);
        if (cancelled) return;
        const sites = sitesRes || [];
        if (!sites.length) { setComparativaRows([]); return; }

        // Initialize all rows immediately so the table shows site names right away
        const orderedResults = sites.map(s => ({
          id: s._id, name: s.name, code: s.code || "", type: s.type || "",
          energia: 0, gei: 0, agua: 0, residuos: 0, efluentes: 0, empleados: 0,
        }));
        setComparativaRows([...orderedResults]);

        const updateRow = (idx, patch) => {
          if (cancelled) return;
          orderedResults[idx] = { ...orderedResults[idx], ...patch };
          setComparativaRows([...orderedResults]);
        };

        await Promise.allSettled(
          sites.map((s, idx) =>
            Promise.allSettled([
              // Energy + GEI — same calculation as GEIDashboard (energy forms + fuels forms, all geiCodes fields, scope1+2+3)
              // Promise.all is safe here because fetchWithRetry never rejects (returns {status:"rejected"} instead)
              Promise.all([
                fetchWithRetry(() => energyController.getEnergyFormsByLastYears(accessToken, s._id, [yearStr])),
                fetchWithRetry(() => fuelsController.getFuelsFormsByLastYears(accessToken, s._id, [yearStr])),
                energyController.getLastUnitFieldsByYear(
                  accessToken, s._id, yearStr,
                  [...ENERGY_GROUPS.electricity, ...ENERGY_GROUPS.renewablesElec, ...ENERGY_GROUPS.fuels]
                ).catch(() => null),
              ]).then(([eRes, fRes, unitsRes]) => {
                const eForms = eRes.status === "fulfilled" ? (eRes.value?.energyForms?.data || []).filter(f => String(f.year) === yearStr) : [];
                const fForms = fRes.status === "fulfilled" ? (fRes.value?.fuelsForms?.data || []).filter(f => String(f.year) === yearStr) : [];
                const siteConfigUnits = unitsRes?.fields || {};
                const conv = (entry, field) => toMWhWithConfig(entry, field, siteConfigUnits);
                let elec = 0, renew = 0, fuels = 0;
                // Mirror total_electricity_kpis combined-first logic from Energy Dashboard.
                // irec_certificates excluded — certificates redistribute ownership, not extra consumption.
                const elecTypeGroups = [
                  ["electricity_standard_combined", "electricity_standard_domestic", "electricity_standard_production", true],
                  ["renewable_energies_ppa_combined", "renewable_energies_ppa_domestic", "renewable_energies_ppa_production", false],
                  ["renewable_energies_on_site_combined", "renewable_energies_on_site_domestic", "renewable_energies_on_site_production", false],
                ];
                elecTypeGroups.forEach(([comb, dom, prod, isElec]) => {
                  const combTotal = eForms.reduce((s, f) => s + conv(f[comb], comb), 0);
                  const groupTotal = combTotal > 0
                    ? combTotal
                    : eForms.reduce((s, f) => s + conv(f[dom], dom) + conv(f[prod], prod), 0);
                  if (isElec) elec += groupTotal; else renew += groupTotal;
                });
                eForms.forEach(form => {
                  ENERGY_GROUPS.fuels.forEach(field => { fuels += conv(form[field], field); });
                });
                let scope1 = 0, scope2 = 0, scope3 = 0;
                [...eForms, ...fForms].forEach(form => {
                  Object.keys(geiCodes).forEach(field => {
                    const entry = form[field];
                    if (entry?.value) {
                      try {
                        const r = calculateEmissionsByType(Number(entry.value), entry.unit, field);
                        if (r?.alcance === 1) scope1 += r.emisiones || 0;
                        else if (r?.alcance === 2) scope2 += r.emisiones || 0;
                        else if (r?.alcance === 3) scope3 += r.emisiones || 0;
                      } catch {}
                    }
                  });
                });
                updateRow(idx, { energia: elec + renew + fuels, gei: scope1 + scope2 + scope3 });
              }),

              // Water
              fetchWithRetry(() => waterController.getWaterFormsByLastYears(accessToken, s._id, [yearStr])).then(wRes => {
                const wForms = wRes.status === "fulfilled" ? (wRes.value?.waterForms?.data || []).filter(f => String(f.year) === yearStr) : [];
                const allWF = Object.values(WATER_GROUPS).flat();
                let agua = 0;
                wForms.forEach(form => allWF.forEach(field => { agua += toM3(form[field]); }));
                updateRow(idx, { agua });
              }),

              // Waste
              fetchWithRetry(() => wasteController.getWasteFormsByLastYears(accessToken, s._id, [yearStr])).then(waRes => {
                const wasteDocs = waRes.status === "fulfilled" ? (waRes.value?.wasteForms?.data || []) : [];
                let residuos = 0;
                wasteDocs.forEach(doc => {
                  WASTE_KEYS.forEach(key => {
                    const field = doc[key];
                    if (field?.value) residuos += toTons(field.value, field.unit || "t");
                  });
                });
                updateRow(idx, { residuos });
              }),

              // Effluent — same logic as effluentSummary: sum all five fields, convert each to m3
              fetchWithRetry(() => effluentController.getEffluentFormsByLastYears(accessToken, s._id, [yearStr])).then(efRes => {
                const effluentDocs = efRes.status === "fulfilled" ? (efRes.value?.effluentForms?.data || []) : [];
                const toM3Eff = (entry, field) => {
                  if (!entry?.value) return 0;
                  const unit = entry.unit || "m3";
                  if (unit === "m3") return Number(entry.value) || 0;
                  return convertEffluent(Number(entry.value), unit, "m3", field) || 0;
                };
                let domestic = 0, industrial = 0, combined = 0, sludge = 0, recycled = 0;
                effluentDocs.forEach(doc => {
                  domestic  += toM3Eff(doc.domestic_effluents,  "domestic_effluents");
                  industrial+= toM3Eff(doc.industrial_effluents,"industrial_effluents");
                  combined  += toM3Eff(doc.combined_effluents,  "combined_effluents");
                  sludge    += toM3Eff(doc.sludge_and_mud,      "sludge_and_mud");
                  recycled  += toM3Eff(doc.recycled_effluents,  "recycled_effluents");
                });
                const efluentes = domestic + industrial + combined + sludge + recycled;
                updateRow(idx, { efluentes });
              }),

              // Workers
              fetchWithRetry(() => socialController.getTotalWorkersByYearsAndPeriod(accessToken, s._id, [yearStr])).then(wkRes => {
                let empleados = 0;
                if (wkRes.status === "fulfilled") {
                  const yearData = wkRes.value?.totalWorkersByPeriod?.[yearStr] || {};
                  const vals = Object.values(yearData).filter(v => v > 0);
                  empleados = vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
                }
                updateRow(idx, { empleados });
              }),
            ])
          )
        );
      } catch {
        if (!cancelled) setComparativaRows([]);
      } finally {
        if (!cancelled) setComparativaLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [company, selectedYear, accessToken, isLoading]);

  // Chart data
  const energySourcesData = useMemo(() => {
    const forms = energyForms.filter(f => String(f.year) === String(selectedYear));
    let electricity = 0, renewables = 0, fuels = 0;
    forms.forEach(form => {
      ENERGY_GROUPS.electricity.forEach(f => { electricity += toMWhWithConfig(form[f], f, energyConfigUnits); });
      ENERGY_GROUPS.renewablesElec.forEach(f => { renewables += toMWhWithConfig(form[f], f, energyConfigUnits); });
      ENERGY_GROUPS.fuels.forEach(f => { fuels += toMWhWithConfig(form[f], f, energyConfigUnits); });
    });
    return [
      { type: "Electricidad", value: parseFloat(electricity.toFixed(2)) },
      { type: "Renovables", value: parseFloat(renewables.toFixed(2)) },
      { type: "Combustibles", value: parseFloat(fuels.toFixed(2)) },
    ].filter(d => d.value > 0);
  }, [energyForms, selectedYear, energyConfigUnits]);

  const energyYearlyData = useMemo(() => {
    return availableYears.slice().sort((a, b) => a - b).map(year => {
      const forms = energyForms.filter(f => String(f.year) === String(year));
      let value = 0;
      forms.forEach(form => {
        [...ENERGY_GROUPS.electricity, ...ENERGY_GROUPS.renewablesElec].forEach(field => {
          value += toMWhWithConfig(form[field], field, energyConfigUnits);
        });
      });
      return { year: String(year), value: parseFloat(value.toFixed(2)) };
    }).filter(d => d.value > 0);
  }, [energyForms, availableYears, energyConfigUnits]);

  const waterSourcesData = useMemo(() => {
    const forms = waterForms.filter(f => String(f.year) === String(selectedYear));
    let redMun = 0, subterr = 0, alt = 0, other = 0;
    forms.forEach(form => {
      WATER_GROUPS.redMunicipal.forEach(f => { redMun += toM3(form[f]); });
      WATER_GROUPS.subterranea.forEach(f => { subterr += toM3(form[f]); });
      WATER_GROUPS.alternativas.forEach(f => { alt += toM3(form[f]); });
      [...WATER_GROUPS.superficial, ...WATER_GROUPS.destilada].forEach(f => { other += toM3(form[f]); });
    });
    return [
      { type: "Red municipal", value: parseFloat(redMun.toFixed(2)) },
      { type: "Subterránea", value: parseFloat(subterr.toFixed(2)) },
      { type: "Alternativas", value: parseFloat(alt.toFixed(2)) },
      { type: "Otras", value: parseFloat(other.toFixed(2)) },
    ].filter(d => d.value > 0);
  }, [waterForms, selectedYear]);

  const waterYearlyData = useMemo(() => {
    const allFields = Object.values(WATER_GROUPS).flat();
    return availableYears.slice().sort((a, b) => a - b).map(year => {
      const forms = waterForms.filter(f => String(f.year) === String(year));
      let value = 0;
      forms.forEach(form => allFields.forEach(field => { value += toM3(form[field]); }));
      return { year: String(year), value: parseFloat(value.toFixed(2)) };
    }).filter(d => d.value > 0);
  }, [waterForms, availableYears]);

  const productionYearlyData = useMemo(() => {
    return availableYears.slice().sort((a, b) => a - b).map(year => {
      const yearStr = String(year);
      const yearDocs = productionForms[yearStr] || [];
      const blType = baselineType[yearStr] || "VPS";
      const prefix = blType === "VPS" ? "VP" : "VF";
      let value = 0;
      yearDocs.forEach(doc => {
        Object.values(doc).forEach(campo => {
          if (campo?.code?.startsWith(prefix) && !campo.code.startsWith(prefix + "S")) {
            value += (Number(campo.value) || 0) * (Number(campo.standardized_coefficient) || 1);
          }
        });
      });
      return { year: yearStr, value: parseFloat(value.toFixed(2)) };
    }).filter(d => d.value > 0);
  }, [productionForms, availableYears, baselineType]);

  const workersYearlyData = useMemo(() => {
    return availableYears.slice().sort((a, b) => a - b).map(year => {
      const yearData = workersByPeriod[String(year)] || {};
      const vals = Object.values(yearData).filter(v => v > 0);
      const avg = vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;
      return { year: String(year), value: avg };
    }).filter(d => d.value > 0);
  }, [workersByPeriod, availableYears]);

  if (isLoading && !energyForms.length && !waterForms.length) return <LoadingSpinner />;

  const row1Boxes = [
    { title: t("total_energy"), value: formatWhole(energyKPIs.total), unit: "MWh", progress: null },
    { title: t("renewable_electric_energy"), value: formatPercentage(energyKPIs.renewablePct), unit: null, progress: energyKPIs.renewablePct },
    { title: t("energy_intensity"), value: formatMagnitude(energyKPIs.intensity), unit: "kWh/unidad", progress: null },
    { title: t("gei_total"), value: formatWhole(geiKPIs.total), unit: "tCO₂eq", progress: null },
  ];

  const row2Boxes = [
    { title: t("total_water"), value: formatWhole(waterKPIs.total), unit: "m³", progress: null },
    { title: t("alternative_water"), value: formatPercentage(waterKPIs.altPct), unit: null, progress: waterKPIs.altPct },
    { title: t("industrial_intensity"), value: formatMagnitude(waterKPIs.intensity), unit: "L/unidad", progress: null },
    { title: t("scope_2"), value: formatWhole(geiKPIs.alcance2), unit: "tCO₂eq", progress: null },
  ];

  return (
    <>
      {/* <div className="general-dash-zoom-bar">
        <button className="general-dash-zoom-btn" onClick={() => handleZoom(-5)} title="Reducir zoom">−</button>
        <span className="general-dash-zoom-label">{zoom}%</span>
        <button className="general-dash-zoom-btn" onClick={() => handleZoom(5)} title="Aumentar zoom">+</button>
      </div> */}
      <div className="general-dash">
      <div ref={dashRef}>
        <div className="general-dash__header">
          {/* <span className="general-dash__title">Dashboard General</span> */}
        </div>

        {fetchError && (
          <div className="general-dash__error-banner">
            No se pudieron cargar todos los datos. Verificá tu conexión e intentá de nuevo.
          </div>
        )}

        {/* Fila 1: KPI boxes energía — año arriba del primer card, export arriba del último */}
        <div className="resumen-wrapper">
          <div className="resumen-grid">
            {row1Boxes.map((box, i) => (
              <div key={i} className={`resumen-card-col${i === 0 ? " resumen-card-col--first" : ""}`}>
                <div className={`resumen-slot-row${i === row1Boxes.length - 1 ? " resumen-slot-row--right" : ""}`}>
                  {i === 0 && (
                    <Dropdown
                      selection
                      compact
                      options={availableYears.map(y => ({ key: y, text: y, value: y }))}
                      value={selectedYear}
                      onChange={(_, { value }) => setSelectedYear(value)}
                      className="year-filter-dropdown"
                    />
                  )}
                  {i === row1Boxes.length - 1 && (
                    <button
                      className="kpi-export-icon-btn"
                      title="Exportar PDF con gráficos"
                      onClick={handleExportPdf}
                      disabled={exportingPdf}
                      data-pdf-exclude="true"
                    >
                      {exportingPdf
                        ? <i className="spinner loading icon" style={{ color: "#555" }} />
                        : <i className="download icon" style={{ color: "#555" }} />}
                    </button>
                  )}
                </div>
                <KpiBox {...box} />
              </div>
            ))}
          </div>
        </div>

        {/* Fila 2: KPI boxes agua */}
        <div className="resumen-wrapper">
          <div className="resumen-grid">
            {row2Boxes.map((box, i) => (
              <div key={i} className="resumen-card-col">
                <KpiBox {...box} />
              </div>
            ))}
          </div>
        </div>

        {/* Tridentes fila 1: Energía */}
        <div className="generalDashboardGrid">
          <TridenteCell title={t("energy_sources")}>
            <PieChart data={energySourcesData} colorMap={ENERGY_SRC_COLORS} labelKeys={ENERGY_SRC_LABEL_KEYS} unit="MWh" loading={isLoading} t={t} />
          </TridenteCell>
          <TridenteCell title={t("electric_energy_consumption")}>
            <BarChart data={energyYearlyData} yLabel="MWh" loading={isLoading} t={t} />
          </TridenteCell>
          <TridenteCell title={t("production")}>
            <BarChart data={productionYearlyData} yLabel={t("units")} loading={isLoading} t={t} />
          </TridenteCell>
        </div>

        {/* Tridentes fila 2: Agua */}
        <div className="generalDashboardGrid">
          <TridenteCell title={t("water_sources")}>
            <PieChart data={waterSourcesData} colorMap={WATER_SRC_COLORS} labelKeys={WATER_SRC_LABEL_KEYS} unit="m³" loading={isLoading} t={t} />
          </TridenteCell>
          <TridenteCell title={t("water_consumption")}>
            <BarChart data={waterYearlyData} yLabel="m³" loading={isLoading} t={t} />
          </TridenteCell>
          <TridenteCell title={t("workers")}>
            <BarChart data={workersYearlyData} yLabel="FTE" loading={isLoading} t={t} />
          </TridenteCell>
        </div>
      </div>

      {/* Comparativa por sitio — capturada en página 2 del PDF */}
      <div ref={comparativaRef}>
        {company && company !== "" && company !== "0" && (isAdmin(role) || isMaster(role)) && sitesList.length > 1 && (
          <SiteComparativaTable
            rows={comparativaRows}
            loading={comparativaLoading}
            currentSite={activeSite !== "all" ? activeSite : null}
            t={t}
          />
        )}
      </div>
    </div>
    </>
  );
}

function KpiBox({ title, value, unit, progress }) {
  const hasProgress = progress !== null;
  return (
    <div className={`resumen-card energy-card${!hasProgress ? " no-progress" : ""}`}>
      <div className="card-meta">
        <div className="card-title-row">
          <span className="card-title">{title}</span>
        </div>
      </div>
      <div className="card-header">
        <span className="card-value">
          {value}
          {unit && <span className="card-unit">{unit}</span>}
        </span>
      </div>
      {hasProgress && (
        <div className="progress-container">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${Math.min(100, Math.max(0, progress || 0))}%` }} />
          </div>
        </div>
      )}
      <div className="empty-space" />
    </div>
  );
}

function TridenteCell({ title, children }) {
  return (
    <div className="chartCard">
      <h3 className="cardTitle">{title}</h3>
      {children}
    </div>
  );
}

function PieChart({ data, colorMap, unit = "", loading = false, labelKeys = {}, t = (k) => k }) {
  const windowWidth = useWindowWidth();
  const [hiddenTypes, setHiddenTypes] = useState([]);
  const label = (type) => t(labelKeys[type] || type);

  const allData = useMemo(() => (data || []).filter(d => d.value > 0), [data]);
  const chartData = useMemo(() => allData.filter(d => !hiddenTypes.includes(d.type)), [allData, hiddenTypes]);
  const totalVisible = chartData.reduce((s, d) => s + d.value, 0);
  const totalAll = allData.reduce((s, d) => s + d.value, 0);

  const isMobile = windowWidth <= 768;
  const donutHeight = isMobile ? 180 : 280;

  if (!allData.length) {
    return loading
      ? <EmptyChartMessage message={t("loading_data")} loading />
      : <EmptyChartMessage message={t("no_data_available")} />;
  }

  const config = {
    data: chartData,
    angleField: "value",
    colorField: "type",
    scale: {
      color: {
        domain: Object.keys(colorMap),
        range: Object.values(colorMap),
      },
    },
    radius: 1,
    innerRadius: 0.6,
    height: donutHeight,
    label: false,
    legend: false,
    animate: false,
    statistic: {
      title: false,
      content: {
        style: { fontSize: "14px", fontWeight: "600", whiteSpace: "pre-line" },
        content: `${t("total")}\n${formatWhole(totalVisible)}`,
      },
    },
    tooltip: {
      items: [
        (datum) => ({
          name: label(datum.type),
          value: `${formatWhole(datum.value)} ${unit} (${totalAll > 0 ? formatPercentage((datum.value / totalAll) * 100) : formatPercentage(0)})`,
        }),
      ],
    },
    interactions: [{ type: "element-active" }],
  };

  return (
    <>
      <div className="chartWrapper"><Pie {...config} /></div>
      <div className="pillsGrid">
        {allData.map(item => {
          const isHidden = hiddenTypes.includes(item.type);
          const color = colorMap[item.type] || "#ccc";
          return (
            <div
              key={item.type}
              className="yearPill"
              onClick={() => setHiddenTypes(prev => isHidden ? prev.filter(y => y !== item.type) : [...prev, item.type])}
              style={{
                backgroundColor: isHidden ? "#E5E7EB" : color,
                color: isHidden ? "#9CA3AF" : "#fff",
                opacity: isHidden ? 0.7 : 1,
                textDecoration: isHidden ? "line-through" : "none",
                cursor: "pointer",
                fontSize: "10px",
              }}
            >
              {label(item.type)}
            </div>
          );
        })}
      </div>
    </>
  );
}

function BarChart({ data, yLabel = "", loading = false, t = (k) => k }) {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth <= 768;
  const barHeight = isMobile ? 180 : 280;

  const allYears = useMemo(() => (data || []).map(d => d.year), [data]);
  const [activeYears, setActiveYears] = useState([]);

  // Sync activeYears when data changes
  useMemo(() => { setActiveYears(allYears); }, [allYears.join(",")]);

  const colorMap = useMemo(() => {
    const map = {};
    allYears.forEach((y, i) => { map[y] = BAR_PALETTE[i] || "#ccc"; });
    return map;
  }, [allYears]);

  const chartData = (data || []).filter(d => activeYears.includes(d.year));

  if (!data?.length) {
    return loading
      ? <EmptyChartMessage message={t("loading_data")} loading />
      : <EmptyChartMessage message={t("no_data_available")} />;
  }

  const config = {
    data: chartData,
    xField: "year",
    yField: "value",
    colorField: "year",
    scale: { color: { domain: Object.keys(colorMap), range: Object.values(colorMap) } },
    height: barHeight,
    style: { radius: 4, maxWidth: 80 },
    legend: false,
    animate: false,
    axis: {
      x: { title: false, labelFontSize: isMobile ? 9 : 11 },
      // Sin título de eje Y en pantalla (se ve en el tooltip); el reporte en
      // PDF sí lo muestra — ver renderBarForPdf en pdfCharts.js.
      y: { title: false, labelFontSize: 10 },
    },
    tooltip: {
      title: (d) => `${t("year")} ${d.year}`,
      items: [(d) => ({ name: yLabel, value: formatWhole(d.value) })],
    },
    interactions: [{ type: "active-region" }],
  };

  return (
    <>
      <div className="chartWrapper"><Column {...config} /></div>
      <div className="pillsRow">
        {allYears.map(year => {
          const active = activeYears.includes(year);
          return (
            <div
              key={year}
              className="yearPill"
              onClick={() => setActiveYears(prev =>
                prev.includes(year) ? prev.filter(y => y !== year) : [...prev, year]
              )}
              style={{
                backgroundColor: active ? colorMap[year] : "#E5E7EB",
                color: active ? "#fff" : "#9CA3AF",
                border: `1px solid ${active ? colorMap[year] : "#D1D5DB"}`,
                opacity: active ? 1 : 0.7,
                textDecoration: active ? "none" : "line-through",
                cursor: "pointer",
                fontSize: "11px",
              }}
            >
              {year}
            </div>
          );
        })}
      </div>
    </>
  );
}

function EmptyMsg() {
  return <div className="general-dash__empty">Sin datos</div>;
}

const COMPARATIVA_COLS = [
  { key: "energia",   labelKey: "energy",    unit: "MWH",   rule: "whole" },
  { key: "gei",       labelKey: "gei",       unit: "TCO₂E", rule: "whole" },
  { key: "agua",      labelKey: "water",     unit: "M³",    rule: "whole" },
  { key: "residuos",  labelKey: "waste",     unit: "TON",   rule: "magnitude" },
  { key: "efluentes", labelKey: "effluent",  unit: "M³",    rule: "whole" },
  { key: "empleados", labelKey: "employees", unit: "FTE",   rule: "whole" },
];

function pillColor(ratio) {
  if (!isFinite(ratio) || ratio <= 0) return { bg: "#e8f0f8", color: "#4a6080" };
  const light = [216, 230, 243];
  const dark  = [26,  58,  92 ];
  const r = Math.min(1, Math.max(0, ratio));
  const bg = light.map((c, i) => Math.round(c + (dark[i] - c) * r));
  return {
    bg: `rgb(${bg[0]},${bg[1]},${bg[2]})`,
    color: r > 0.5 ? "#ffffff" : "#1a3a5c",
  };
}

function SiteComparativaTable({ rows, loading, currentSite, t }) {
  if (loading) return (
    <div className="comparativa-section">
      <div className="comparativa-loading">{t("loading_data")}</div>
    </div>
  );
  if (!rows || rows.length === 0) return (
    <div className="comparativa-section">
      <div className="comparativa-loading">{t("no_data_available")}</div>
    </div>
  );

  const maxPerCol = {};
  COMPARATIVA_COLS.forEach(col => {
    maxPerCol[col.key] = Math.max(...rows.map(r => r[col.key] || 0));
  });

  return (
    <div className="comparativa-section">
      <div className="comparativa-header">
        <span className="comparativa-title">{t("site_comparative_title")}</span>
        <span className="comparativa-legend">{t("site_comparative_legend")}</span>
      </div>
      <div className="comparativa-table-wrap">
        <table className="comparativa-table">
          <thead>
            <tr>
              <th className="comparativa-th comparativa-th--site">{t("site").toLocaleUpperCase()}</th>
              {COMPARATIVA_COLS.map(col => (
                <th key={col.key} className="comparativa-th">
                  {t(col.labelKey).toLocaleUpperCase()} <span className="comparativa-th__unit">({col.unit})</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.id} className={`comparativa-tr${row.id === currentSite ? " comparativa-tr--current" : ""}`}>
                <td className="comparativa-td comparativa-td--site">
                  <span className="comparativa-site-name">{row.name}</span>
                  {(row.code || row.type) && (
                    <span className="comparativa-site-meta">
                      {[row.code, row.type].filter(Boolean).join(" · ")}
                    </span>
                  )}
                </td>
                {COMPARATIVA_COLS.map(col => {
                  const val = row[col.key] || 0;
                  const ratio = maxPerCol[col.key] > 0 ? val / maxPerCol[col.key] : 0;
                  const { bg, color } = pillColor(ratio);
                  const display = val > 0
                    ? (col.rule === "whole" ? formatWhole(val) : formatMagnitude(val))
                    : "-";
                  return (
                    <td key={col.key} className="comparativa-td">
                      <span className="comparativa-pill" style={{ background: bg, color }}>
                        {display}
                      </span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
